// SessionLogView page
import React, { useState } from "react";
import { Container, Row, Col, Button } from "reactstrap";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import styles from "modules/Library/AdverseEvent/css/adverseEvent.module.scss";
import tabStyles from "modules/PrintLabel/MainPrintListTabs/css/labeltypetabs.module.scss";
import ac from "../css/accessioning.module.scss";
import CommonTableIndexWise from "Components/Common/CommonTableIndexWise";
import { ACCESSIONING_PATHS, SCANNING_PATHS } from "Routes/paths";
import {
  useScanSampleCollectionService,
  useScanSampleSeperationService,
  useSessionService,
} from "modules/Session/Session/api/useSession";
import CommonSearch from "Components/Common/CommonSearch";
import { getSessionLogColumns } from "../helper/sessionlogconstant";

const SessionLogView: React.FC = () => {
  document.title = "Session Log | ZENTIXS VISTA";
  
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  
  const isScanning = location.pathname.startsWith("/scanning");
  const listPath = isScanning ? SCANNING_PATHS.VIEW : ACCESSIONING_PATHS.LIST;

  const [searchText, setSearchText] = useState<string>("");
  const [activeTab, setActiveTab] = useState("subjectToVacutainer");

  const [currentPageNumber, setCurrentPageNumber] = useState(1);

  const { SelectQuery: sessionQuery } = useSessionService({
    sessionid: id ? parseInt(id) : 0,
  });

  const sessionData = sessionQuery?.data?.data?.[0];

  const stats = [
    {
      title: "SUBJECTS ACCESSIONED",
      value: `${sessionData?.completed_subjects || 0} / ${sessionData?.total_subjects || sessionData?.totalsubjects || 0}`,
      icon: <i className="ri-group-line" />,
      bgColor: "#ecfdf5",
      iconColor: "#10b981",
    },
    {
      title: "VACUTAINERS MATCHED",
      value: `${sessionData?.completed_vaccutainers || 0} / ${sessionData?.total_vaccutainers || sessionData?.totalsubjects || 0}`,
      icon: <i className="ri-test-tube-line" />,
      bgColor: "#fff7ed",
      iconColor: "#f59e0b",
    },
    {
      title: "ALIQUOTS SEPARATED",
      value: `${sessionData?.completed_aliquots || 0} / ${sessionData?.total_aliquots_count || sessionData?.totalsubjects * sessionData?.total_aliquots || sessionData?.total_aliquots || 0}`,
      icon: <i className="ri-flask-line" />,
      bgColor: "#ecfdf5",
      iconColor: "#10b981",
    },
  ];

  const { SelectAllQuery: collectionQuery } = useScanSampleCollectionService({
    sessionid: id ? parseInt(id) : 0,
    searchtext: searchText,
    currentpage: currentPageNumber,
    pagesize: 10,
  });

  const { SelectAllQuery: separationQuery } = useScanSampleSeperationService({
    sessionid: id ? parseInt(id) : 0,
    searchtext: searchText,
    currentpage: currentPageNumber,
    pagesize: 10,
  });

  const activeQuery =
    activeTab === "subjectToVacutainer" ? collectionQuery : separationQuery;
  const logData = activeQuery?.data?.data || [];
  const totalRecords = logData?.[0]?.total_count || 0;

  const columns = getSessionLogColumns(activeTab, ac);

  const onBack = () => {
    navigate(listPath);
  };

  const onSearch = (text: string | null) => {
    setSearchText(text ?? "");
    setCurrentPageNumber(1);
  };

  return (
    <div className={styles.dashboardContainer}>
      <Container fluid>
        {/* Page Header */}
        <div className="d-flex justify-content-between align-items-center mt-4 mb-4 px-2">
          <div className="d-flex align-items-center gap-3">
            <Button
              color="light"
              className="bg-white border rounded-3 px-2 py-1 shadow-sm"
              onClick={onBack}
            >
              <i className="ri-arrow-left-line text-dark fs-18"></i>
            </Button>
            <div>
              <div className="d-flex align-items-center gap-2 mb-1">
                <h5 className={`mb-0 ${styles.pageHeaderTitle}`}>
                  Chronological Scan Log:{" "}
                  {sessionData?.sessionno || sessionData?.sessionid || id}
                </h5>
                <span
                  className="badge bg-success bg-opacity-10 text-success rounded-pill px-3 py-1 fw-600"
                  style={{ fontSize: "11px" }}
                >
                  COMPLETED
                </span>
              </div>
              <p className={styles.pageHeaderSubtitle}>
                {sessionData?.studycode} | Period {sessionData?.period_number} |
                Timepoint {sessionData?.timepoint_number}
              </p>
            </div>
          </div>
        </div>

        {/* Stat Cards */}
        <Row className="g-3 mb-4 px-1">
          {stats.map((stat, idx) => (
            <Col key={idx} md={4}>
              <div className={styles.statCard}>
                <div
                  className={styles.iconWrapper}
                  style={{
                    backgroundColor: stat.bgColor,
                    color: stat.iconColor,
                  }}
                >
                  {stat.icon}
                </div>
                <div className={styles.label}>{stat.title}</div>
                <div className={styles.value}>{stat.value}</div>
              </div>
            </Col>
          ))}
        </Row>

        {/* Log Panel */}
        <div className={styles.listPanel}>
          <div className={styles.panelHeader}>
            <div>
              <h2>
                <i className="ri-list-check-2 text-primary fs-18 me-2"></i>
                Chronological Scan Log
              </h2>
              <p
                className="text-muted mb-0 mt-1"
                style={{ fontSize: "12px", fontWeight: 500 }}
              >
                Detailed real-time log of all barcodes scanned and events
                recorded during this processing session.
              </p>
            </div>
            <CommonSearch
              onSearch={onSearch}
              searchText={searchText}
              placeholder="Search Barcode..."
            />
          </div>

          {/* Navigation Tabs */}
          <div className={tabStyles.tabContainer}>
            <button
              type="button"
              className={`${tabStyles.tabBtn} ${activeTab === "subjectToVacutainer" ? tabStyles.active : ""}`}
              onClick={() => {
                setActiveTab("subjectToVacutainer");
                setSearchText("");
                setCurrentPageNumber(1);   // ← reset page
              }}
            >
              Subject to Vacutainer
            </button>
            <button
              type="button"
              className={`${tabStyles.tabBtn} ${activeTab === "vacutainerToAliquot" ? tabStyles.active : ""}`}
              onClick={() => {
                setActiveTab("vacutainerToAliquot");
                setSearchText("");
                setCurrentPageNumber(1);   // ← reset page
              }}
            >
              Vacutainer to Aliquot
            </button>
          </div>

          <div className="px-4 pb-2 mt-3">
            <CommonTableIndexWise
              columns={columns}
              data={logData}
              pageSize={10}
              currentPage={currentPageNumber}
              totalRecords={totalRecords}
              onPageChange={setCurrentPageNumber}
              hideAction={true}
              loading={
                collectionQuery.isFetching ||
                separationQuery.isFetching ||
                sessionQuery.isFetching
              }
            />
          </div>
        </div>
      </Container>
    </div>
  );
};

export default SessionLogView;
